// Общий блок «Записать клиента на урок» — один код для звонильщика (CallerDashboard,
// сделка) и переписчика (WriterDashboard, диалог Instagram / карточка клиента).
// Антон 03.09.2026: «переписчик такой же, как звонильщик, просто закрывает лидов в
// переписке — блок записи должен быть идентичный». Поэтому цена/скидка/жёлтая
// кнопка/зелёный «Записан»/модалка вынесены сюда из CallerDashboard, а не скопированы.
import { useState, useEffect } from 'react'
import api from '../api'
import TrialSignupModal from './TrialSignupModal'
import { formatTrialDate } from './TrialsBoard'

// ─────────── Цена по классу (зеркало сетки платформы) ───────────
export const LEADER_PRICE = { grade1_8: 1200, oge: 1400, ege: 1600 }
// Входная цена обычного преподавателя — ступень «0–4 ученика» (02.10.2026).
// Была 600 ₽, пока цена не зависела от загрузки.
export const BASE_PRICE = 800
export function classToTier(classNum) {
  const s = String(classNum || '').trim()
  const m = s.match(/^(\d{1,2})/)
  if (m) {
    const n = Number(m[1])
    if (n === 9) return 'oge'
    if (n >= 10) return 'ege'
    return 'grade1_8'
  }
  if (/колледж|техникум/i.test(s)) return 'ege'
  return 'grade1_8'
}
export function leaderPriceMirror(classNum) {
  return LEADER_PRICE[classToTier(classNum)]
}
export function isNonEmpty(v) {
  if (v == null) return false
  if (Array.isArray(v)) return v.length > 0
  return String(v).trim() !== ''
}

// ─────────── Цена урока, которую менеджер называет клиенту ───────────
// До 03.11.2026 здесь была форма «Предложить скидку»: менеджер выбирал класс, а
// цену считала формула — 800/900/1000 ₽ (входная цена обычного преподавателя),
// другой цифры поставить было нельзя. В живом разговоре этого не хватает:
// «клиент не берёт за 1600 — предлагаю 1500», а такой цены в форме не было
// (Роман 03.11.2026). Теперь менеджер выбирает цену сам, как сопроводитель в
// «Записать на платное», а «Применить» фиксирует её на сделке: с этой ценой
// идёт и запись, и карточка ученика (цена связки ученик↔преподаватель).
// Шаг 100 ₽ и границы 500–2000 ₽ — те же, что у сопроводителя (SupportDashboard).
export const MANAGER_PRICE_OPTIONS = (() => { const a = []; for (let p = 500; p <= 2000; p += 100) a.push(p); return a })()
export const discountSelectStyle = {
  borderColor: '#FDE68A',
  backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23B45309' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>\")",
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center',
}

export function PriceForm({ onClose, onSave, classNum }) {
  const standard = leaderPriceMirror(classNum)
  const [price, setPrice] = useState(standard)
  const discount = price < standard
  return (
    <div className="rounded-2xl p-4" style={{ background: '#FFF7ED', border: '1px solid #FDE68A' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[13px] font-semibold" style={{ color: '#92400E' }}>Цена урока для этого клиента</div>
        <button type="button" onClick={onClose} className="hover:opacity-70" style={{ color: 'rgba(180,83,9,0.5)' }}>✕</button>
      </div>
      <label className="block">
        <div className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: 'rgba(180,83,9,0.5)' }}>Цена за урок</div>
        <select value={price} onChange={e => setPrice(Number(e.target.value))}
          className="w-full text-[13px] bg-white border rounded-lg px-3 py-2 outline-none appearance-none pr-7"
          style={discountSelectStyle}>
          {MANAGER_PRICE_OPTIONS.map(p => (
            <option key={p} value={p}>{p} ₽{p === standard ? ' — стандарт по классу' : ''}</option>
          ))}
        </select>
      </label>
      <div className="flex items-center gap-2 mt-3">
        <span className="text-[11px]" style={{ color: 'rgba(180,83,9,0.5)' }}>Получится:</span>
        <span className="inline-flex items-center gap-1.5 text-[13px] font-bold px-2.5 py-1 rounded-full"
          style={{ background: 'white', color: '#B45309', border: '1px solid #FDE68A' }}>
          {price} ₽ / урок
        </span>
        <span className="text-[11px]" style={{ color: 'rgba(180,83,9,0.6)' }}>
          {discount ? `скидка ${standard - price} ₽ от стандарта ${standard} ₽`
            : price > standard ? `выше стандарта ${standard} ₽`
            : 'стандартная цена'}
        </span>
      </div>
      <div className="text-[11px] mt-2" style={{ color: 'rgba(180,83,9,0.6)' }}>
        {discount
          ? `Со скидкой записать можно только на преподавателя платформы: Лидеру урок дешевле ${standard} ₽ назначить нельзя, в списке его не будет. У занятых преподавателей своя цена — она видна на карточке.`
          : 'Стандартная цена — в списке будут Лидеры по цене класса (1200 / 1400 / 1600 ₽).'}
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button type="button" className="text-[13px] font-semibold text-white px-4 py-2 rounded-lg" style={{ background: '#F59E0B' }}
          onClick={() => onSave(String(price), '')}>
          Применить
        </button>
        <button type="button" className="text-[13px]" style={{ color: 'rgba(180,83,9,0.5)' }} onClick={onClose}>Отмена</button>
      </div>
    </div>
  )
}

// Кнопка «Изменить цену» — тот же чип-стиль, что причины отказа в RejectForm.
export function PriceOverrideToggle({ onSet, classNum }) {
  const [open, setOpen] = useState(false)
  if (!open) {
    return (
      <button type="button" className="text-[12px] font-medium px-3 py-1.5 rounded-full transition-colors mt-1.5"
        style={{ background: 'white', color: '#B45309', border: '1px solid #FDE68A' }}
        onClick={() => setOpen(true)}>
        Изменить цену
      </button>
    )
  }
  return <PriceForm classNum={classNum} onClose={() => setOpen(false)} onSave={(price, age) => { onSet(String(price), String(age)); setOpen(false) }} />
}

// Значение строки «Цена урока»: зелёная плашка тарифа по классу + «Предложить скидку»,
// либо оранжевая плашка скидки + «сбросить».
export function PriceLine({ classNum, priceOverride, onSet, onReset }) {
  return isNonEmpty(priceOverride) ? (
    <>
      <span className="inline-flex items-center gap-1.5 text-[13px] font-bold px-2.5 py-1 rounded-full"
        style={{ background: '#FFF7ED', color: '#B45309', border: '1px solid #FDE68A' }}>
        {priceOverride} ₽ / урок{Number(priceOverride) < leaderPriceMirror(classNum) ? ' (со скидкой)' : ''}
      </span>
      <div className="text-[11px] text-slate-400 mt-1">
        обычная цена {leaderPriceMirror(classNum)} ₽ ·{' '}
        <button type="button" className="underline hover:text-slate-600" onClick={onReset}>
          сбросить
        </button>
      </div>
    </>
  ) : (
    <>
      <span className="inline-flex items-center gap-1.5 text-[13px] font-bold px-2.5 py-1 rounded-full w-fit"
        style={{ background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0' }}>
        {leaderPriceMirror(classNum)} ₽ / урок
      </span>
      <PriceOverrideToggle onSet={onSet} classNum={classNum} />
    </>
  )
}

// Название жёлтой кнопки: со скидкой (от 800/900/1000) запись пойдёт только на
// платформенного препода, а у платформенных пробных нет — честно «Записать на урок».
export function trialButtonLabel(priceOverride, classNum) {
  const discount = isNonEmpty(priceOverride) && Number(priceOverride) < leaderPriceMirror(classNum)
  return discount ? 'Записать на урок' : 'Записать на пробный'
}

// Жёлтая кнопка записи — одна и та же у звонильщика (ActionRow) и переписчика.
export function TrialButton({ label, disabled, title, onClick, className = '' }) {
  return (
    <button onClick={() => !disabled && onClick && onClick()}
      disabled={disabled}
      title={disabled ? (title || 'Сначала заполните анкету') : ''}
      className={'h-12 rounded-xl font-semibold text-[14px] text-white transition-transform active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed ' + className}
      style={{ background: '#FBBF24', boxShadow: '0 1px 0 rgba(180,83,9,0.2)' }}>
      {label || 'Записать на пробный'}
    </button>
  )
}

// Телефон клиента человеку: 9530232692 → +7 (953) 023-26-92.
function prettyPhone(raw) {
  const d = String(raw || '').replace(/\D/g, '').slice(-10)
  if (d.length !== 10) return String(raw || '')
  return `+7 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8)}`
}

// Кнопка «Отправить SMS со ссылкой» в зелёном блоке «Записан» (Антон 09.09.2026).
// Раньше платформа сама слала SMS с кодом («Код для входа… 1234») — клиент не понимал,
// что с ним делать. Теперь менеджер жмёт сам, клиенту уходит «Котангенс: урок 10.09
// в 16:00 мск. Ваш кабинет: ctg.school/c/…» — по ссылке кабинет открывается без кода.
export function SmsLinkRow({ bookingId, phone }) {
  const [state, setState] = useState({ status: 'idle', at: null, error: null })
  const [busy, setBusy] = useState(false)
  // 15.09.2026: платформа сама шлёт SMS со ссылкой сразу после записи менеджером — подтягиваем
  // состояние, чтобы менеджер видел «SMS отправлена», а не кнопку (и не слал второй раз).
  useEffect(() => {
    if (!bookingId) return undefined
    let alive = true
    const load = () => api.get(`/api/platform/bookings/${bookingId}/sms-link`).then(({ data }) => {
      if (!alive || !data || !data.sent_at) return
      const at = new Date(String(data.sent_at).replace(' ', 'T') + (String(data.sent_at).includes('Z') ? '' : 'Z'))
      setState(s => (s.status === 'idle' ? { status: data.error ? 'error' : 'sent', at, error: data.error || null } : s))
    }).catch(() => {})
    load()
    const t1 = setTimeout(load, 5000)
    const t2 = setTimeout(load, 25000)
    return () => { alive = false; clearTimeout(t1); clearTimeout(t2) }
  }, [bookingId])
  if (!bookingId) return null

  const send = async () => {
    if (busy) return
    setBusy(true)
    try {
      const { data } = await api.post(`/api/platform/bookings/${bookingId}/sms-link`)
      setState({ status: data && data.ok ? 'sent' : 'error', at: new Date(), error: (data && data.error) || null })
    } catch (e) {
      const body = (e.response && e.response.data) || {}
      setState({ status: 'error', at: new Date(), error: body.error || e.message || 'не удалось отправить' })
    } finally { setBusy(false) }
  }

  const hhmm = state.at ? String(state.at.getHours()).padStart(2, '0') + ':' + String(state.at.getMinutes()).padStart(2, '0') : ''
  const btn = {
    fontSize: 13, fontWeight: 700, borderRadius: 10, padding: '7px 12px',
    border: '1px solid #BAE6FD', background: '#F0F9FF', color: '#0369A1', cursor: busy ? 'default' : 'pointer',
    opacity: busy ? 0.6 : 1,
  }
  const ghost = { ...btn, background: 'transparent', borderColor: '#E2E8F0', color: '#64748B', fontWeight: 600 }

  if (state.status === 'error') {
    return (
      <div style={{ paddingLeft: 32, marginTop: 10 }}>
        <div style={{ borderRadius: 12, padding: '10px 12px', background: '#FFF7ED', border: '1px solid #FDE68A', color: '#B45309', fontSize: 12.5 }}>
          SMS не ушла: {state.error}. Продиктуйте адрес ctg.school и скажите, что вход по номеру телефона.
        </div>
        <div style={{ marginTop: 10 }}>
          <button type="button" style={ghost} onClick={send} disabled={busy}>{busy ? 'Отправляю…' : 'Попробовать ещё раз'}</button>
        </div>
      </div>
    )
  }
  if (state.status === 'sent') {
    return (
      <div className="flex items-center gap-2.5 flex-wrap" style={{ paddingLeft: 32, marginTop: 10 }}>
        <span style={{ fontSize: 12.5, color: '#059669' }}>SMS отправлена в {hhmm}{phone ? ' на ' + prettyPhone(phone) : ''}</span>
        <button type="button" style={ghost} onClick={send} disabled={busy}>{busy ? 'Отправляю…' : 'Отправить ещё раз'}</button>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-2.5 flex-wrap" style={{ paddingLeft: 32, marginTop: 10 }}>
      <button type="button" style={btn} onClick={send} disabled={busy}>{busy ? 'Отправляю…' : 'Отправить SMS со ссылкой'}</button>
      <span style={{ fontSize: 12, color: '#64748B' }}>Скажите клиенту: сейчас придёт SMS, по ссылке заходите в день урока</span>
    </div>
  )
}

// Зелёные блоки «Записан — …» под анкетой после успешной записи.
export function BookedTrialsList({ items }) {
  if (!items || !items.length) return null
  return (
    <>
      {items.map((b, i) => (
        <div key={i} className="rounded-2xl p-4" style={{ background: '#ECFDF5', border: '1px solid #A7F3D0' }}>
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: '#A7F3D0', color: '#047857' }}>✓</span>
            <div className="text-[13px] font-bold" style={{ color: '#047857' }}>Записан — {b.childLabel ? `${b.childLabel}, ` : ''}{b.subject}</div>
          </div>
          <div className="text-[12.5px]" style={{ color: '#059669', paddingLeft: 32, marginTop: 4 }}>
            {formatTrialDate(b.date)}, {b.time} · препод {b.teacherName || '—'}
          </div>
          <SmsLinkRow bookingId={b.bookingId} phone={b.phone} />
        </div>
      ))}
    </>
  )
}

// ─────────── Готовый блок для переписчика ───────────
// client: { mkId | newClientId, name, phone, grade } — клиент карточки/диалога или анкета нового.
// onBook(params) → Promise<{ sms_sent } | null>: params как у звонильщика (handleTrial):
// teacherMkId, teacherName, subject, level, date, time, comment, priceOverride, childPhone.
// Скидка живёт в состоянии блока (у звонильщика — в анкете сделки).
export default function LessonBookingBlock({ client, clientLabel, subject, onBook, compact }) {
  const [priceOverride, setPriceOverride] = useState('')
  const [priceOverrideAge, setPriceOverrideAge] = useState('')
  const [open, setOpen] = useState(false)
  const [booked, setBooked] = useState([])
  const [busy, setBusy] = useState(false)

  // Клиент из карточки (mkId) или анкета «+ Новый клиент» (newClientId) — ученик на платформе
  // появится при записи. Без телефона записать нельзя.
  const ready = !!(client && client.phone && (client.mkId || client.newClientId))
  const classNum = client && client.grade != null ? String(client.grade) : ''

  const handleSubmit = async (params) => {
    if (busy) return
    setBusy(true)
    try {
      const result = await onBook({
        ...params,
        priceOverride: isNonEmpty(priceOverride) ? Number(priceOverride) : null,
        childPhone: '',
      })
      if (result) {
        setOpen(false)
        setBooked(prev => [...prev, {
          subject: params.subject, date: params.date, time: params.time,
          teacherName: params.teacherName, childLabel: null,
          // id брони + телефон — для кнопки «Отправить SMS со ссылкой» (09.09.2026)
          bookingId: (result.booking && result.booking.id) || null,
          phone: (client && client.phone) || null,
        }])
      }
    } finally { setBusy(false) }
  }

  return (
    <div className={compact ? 'space-y-2.5' : 'space-y-2.5 mt-4'}>
      {/* Не <label> — см. CallerDashboard: клик по строке уезжал на «сбросить». */}
      <div className="flex items-start gap-3 py-2 border-b border-slate-100">
        <div className="w-[140px] shrink-0 text-[12px] text-slate-500 leading-tight pt-[6px]">Цена урока</div>
        <div className="flex-1 min-w-0">
          <PriceLine classNum={classNum} priceOverride={priceOverride}
            onSet={(price, age) => { setPriceOverride(price); setPriceOverrideAge(age) }}
            onReset={() => { setPriceOverride(''); setPriceOverrideAge('') }} />
        </div>
      </div>
      <BookedTrialsList items={booked} />
      <TrialButton className="w-full" label={trialButtonLabel(priceOverride, classNum)} disabled={!ready}
        title="Сначала создайте ученика (имя и телефон)" onClick={() => setOpen(true)} />
      <TrialSignupModal
        open={open}
        onClose={() => setOpen(false)}
        clientLabel={clientLabel || (client && client.name) || ''}
        initial={{ subject: subject || '', classNum, childName: client && client.name, priceOverride, priceOverrideAge }}
        onSubmit={handleSubmit}
      />
    </div>
  )
}
